/**
 * Google Analytics 4 integration for the public marketing pages only.
 *
 * Privacy boundary (do not weaken this without re-reading
 * docs/SEO_AUDIT.md / the analytics section of the SEO initiative this
 * belongs to): this file must never be imported from, or called with data
 * from, any authenticated part of the app. It tracks marketing-site
 * behavior only - page views and CTA clicks on public pages - and must
 * never receive asset data, employee data, user emails, or any other
 * company-confidential information as an event parameter.
 *
 * No-ops entirely if VITE_GA_MEASUREMENT_ID isn't set, so this has zero
 * effect (not even a network request) until a real GA4 property is
 * configured - see docs/GOOGLE_SEARCH_CONSOLE_SETUP.md's sibling analytics
 * setup notes for how to obtain one.
 *
 * This var controls ONLY this file. The authenticated app's own analytics
 * (utils/productAnalytics.ts) reads a separate VITE_PRODUCT_ANALYTICS_ID -
 * setting this one must never also turn that one on.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
let initialized = false;

function ensureLoaded() {
  if (!measurementId || initialized) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) { window.dataLayer!.push(args); };
  window.gtag("js", new Date());
  // send_page_view: false - this app tracks page views explicitly via
  // trackPageView() on each marketing page's mount, since it isn't a
  // traditional multi-page site GA's default auto-tracking assumes.
  window.gtag("config", measurementId, { send_page_view: false, anonymize_ip: true });
}

/** Call once per marketing page view - path should be a plain route like
 * "/pricing" or "/features/asset-tracking", never anything containing a
 * user identifier, email, or company name. */
export function trackPageView(path: string, title: string) {
  if (!measurementId) return;
  ensureLoaded();
  window.gtag?.("event", "page_view", { page_path: path, page_title: title });
}

/** Call for a marketing-site CTA click. `label` should be a static,
 * generic description of the button ("start_free_hero", "book_demo_nav") -
 * never anything derived from user input. */
export function trackCtaClick(label: string) {
  if (!measurementId) return;
  ensureLoaded();
  window.gtag?.("event", "cta_click", { cta_label: label });
}
